// React imports:
import React, { useEffect, useMemo, useState } from 'react';

// 3rd party library imports:
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faPenToSquare, faPlusCircle, faSearchPlus } from '@fortawesome/pro-solid-svg-icons';
import { Toast, ToastContainer } from 'react-bootstrap';
import Bugsnag from '@bugsnag/js';
import { subMonths } from 'date-fns';

// Component imports:
import AddIndividualModal from './AddIndividualModal/AddIndividualModal';
import Alert, { AlertType } from '../../ui-elements/Alert/Alert';
import AutoRiskScoreModal from './AutoRiskScoreModal';
import Button from '../../ui-elements/Button/Button';
import Checkbox from '../../ui-elements/Input/Checkbox';
import DropSelect from '../../ui-elements/DropSelect/DropSelect';
import EditIndividualModal from './EditIndividualModal';
import GetKYCOverviewIndividualsModal from './GetKYCOverview/GetKYCOverviewIndividualsModal';
import Input from '../../ui-elements/Input/Input';
import Label from '../../ui-elements/Label/Label';
import Link from '../../ui-elements/Link/Link';
import Loading from '../../ui-elements/Loading/Loading';
import RiskScoreModal from './RiskScoreModal/RiskScoreModal';
import Table from '../../ui-elements/Table/Table';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';

// Service imports:
import { GET_KELDAN_API_URL, TOAST_DELAY } from '../../services/config';
import { ErrorMessages } from '../../services/errorMessages';
import { getTableDate, isSSN, translateSurveyStatus } from '../../services/utils';

// Static data imports:
import { CountryList } from '../../assets/staticData/CountryList';
import { riskToLabelTypeMap } from './CompaniesTable';
import SendNotificationModal from './SendNotificationModal';

// Type imports:
import { Fetched } from '../../types/Types';
import {
    KnowYourIndividual,
    LastAnsweredFilter,
    RiskScoreFilter,
    RiskScoreFormValues,
    ServiceCompanyEmployee,
    StatusFilter
} from '../../types/DueDiligenceTypes';

interface IProps {
    individuals: Fetched<KnowYourIndividual[]>;
    setIndividuals: React.Dispatch<React.SetStateAction<Fetched<KnowYourIndividual[]>>>;
    setShowKYCOverview: (show: boolean) => void;
    employees: ServiceCompanyEmployee[];
    individualKYCPrice: number;
    showKYCOverviewModal: boolean;
}

const IndividualsTable: React.FC<IProps> = ({
    individuals,
    setIndividuals,
    setShowKYCOverview,
    employees,
    individualKYCPrice,
    showKYCOverviewModal
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filteredData, setFilteredData] = useState<KnowYourIndividual[]>();

    // Filter states
    const [lastAnsweredFilter, setLastAnsweredFilter] = useState<LastAnsweredFilter>('all');
    const [riskScoreFilter, setRiskScoreFilter] = useState<RiskScoreFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [supervisorFilter, setSupervisorFilter] = useState<ServiceCompanyEmployee | null>(null);

    const [addIndividualShow, setAddIndividualModal] = React.useState<boolean>(false);

    const [riskScoreModal_isOpen, setRiskScoreModal_isOpen] = useState(false);
    const [riskScoreModal_data, setRiskScoreModal_data] = useState<RiskScoreFormValues | undefined>(undefined);

    const [editIndividualModal_isOpen, setEditIndividualModal_isOpen] = useState(false);
    const [editIndividualModal_data, setEditIndividualModal_data] = useState<KnowYourIndividual | null>(null);

    const [sendNotificationModal_isOpen, setSendNotificationModal_isOpen] = useState(false);
    const [autoRiskScoreModel_isOpen, setAutoRiskScoreModel_isOpen] = useState(false);

    const [toastShow, setToastShow] = useState(false);
    const [toastType, setToastType] = useState<AlertType>("success");
    const [toastMessage, setToastMessage] = useState("");
    const [excelDownloading, setExcelDownloading] = useState(false);

    const fetchIndividuals = async () => {
        try {
            setIsLoading(true);
            const url = `${GET_KELDAN_API_URL()}/DueDiligence/GetIndividualsOverview`;
            const response = await fetch(url, {
                cache: 'no-cache',
            });
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const data = await response.json();
            setIndividuals(data.map((i: KnowYourIndividual) => ({ ...i, checked: false })));
        } catch (error) {
            if (error instanceof Error) Bugsnag.notify(error);
            setIndividuals(new Error(ErrorMessages.NetworkError));
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchIndividuals();
    }, []);

    // Add these two useEffect hooks in your IndividualsTable component
    // Place them after your existing useEffect hooks (around line 118, after the fetchIndividuals useEffect)

    // Refresh data when user navigates back to the page (back button)
    useEffect(() => {
        /**
         * The pageshow event fires whenever the page becomes visible to the user.
         * The 'persisted' property indicates if the page was loaded from the browser's
         * back-forward cache (bfcache), which happens when users click the back button.
         * When persisted is true, we know the user navigated back, so we refresh the data
         * to ensure they see the most current information from other team members.
         */
        const handlePageShow = (event: PageTransitionEvent) => {
            // Only refresh if the page was loaded from cache (back button used)
            if (event.persisted) {
                fetchIndividuals();
            }
        };

        window.addEventListener('pageshow', handlePageShow);

        return () => {
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, []);

    // Apply all filters when dropdown filters change
    useEffect(() => {
        // Don't show loading indicator for dropdown filter changes
        // as they should be nearly instant

        // Skip the effect if data isn't loaded yet
        if (!individuals || individuals instanceof Error) return;

        // Use the direct filtering approach to avoid stale closures
        const filtered = applyFiltersDirectly(
            [...individuals],
            searchTerm,
            lastAnsweredFilter,
            riskScoreFilter,
            statusFilter,
            supervisorFilter
        );

        setFilteredData(filtered.sort((a, b) => Number(b.active) - Number(a.active)));
    }, [individuals, searchTerm, lastAnsweredFilter, riskScoreFilter, statusFilter, supervisorFilter]);

    // Debounced search handler (with loading indicator)
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const anyChecked = useMemo(() => {
        if (!individuals || individuals instanceof Error) return false;
        return individuals.some(i => i.checked);
    }, [individuals]);

    const onlyAutoEligibleChecked = useMemo(() => {
        if (!individuals || individuals instanceof Error) return false;
        const checkedIndividuals = individuals.filter(i => i.checked);
        return checkedIndividuals.every(i => i.questionsAnswered && i.riskscore === null) && checkedIndividuals.length > 0;
    }, [individuals]);

    const onlyNotificationEligibleChecked = useMemo(() => {
        if (!individuals || individuals instanceof Error) return false;
        const checkedIndividuals = individuals.filter(i => i.checked);
        return checkedIndividuals.every(i => !i.questionsAnswered && i.riskscoreId && i.status !== 'Villa') && checkedIndividuals.length > 0;
    }, [individuals]);

    // Handler to add a new individual
    const handleAddIndividual = (newIndividual: KnowYourIndividual) => {
        setIndividuals(prev => !(prev instanceof Error) && prev !== null ? [newIndividual, ...prev] : [newIndividual]);
    };

    const handleUpdateIndividual = (individualId: number, updates: Partial<KnowYourIndividual>) => {
        setIndividuals(prev => {
            if (prev instanceof Error || prev === null) return prev;
            return prev.map(individual =>
                individual.individualCustomerId === individualId
                    ? { ...individual, ...updates }
                    : individual
            );
        });
    };

    // Shared function to apply filters and update state
    const updateFiltersAndState = (currentSearchTerm: string) => {
        if (!individuals || individuals instanceof Error) {
            // Handle error state
            setFilteredData([]);
            return;
        }
        // Create a local copy of the data to filter
        const dataToFilter = [...(individuals || [])];

        // Force immediate filtering with current state values
        const filtered =
            dataToFilter.length > 0
                ? applyFiltersDirectly(
                    dataToFilter,
                    currentSearchTerm,
                    lastAnsweredFilter,
                    riskScoreFilter,
                    statusFilter,
                    supervisorFilter
                )
                : [];

        // Update filtered data state
        setFilteredData(filtered.sort((a, b) => Number(b.active) - Number(a.active)));

        // Turn off loading state
        setIsLoading(false);
    };

    const applyFiltersDirectly = (
        data: KnowYourIndividual[],
        search: string,
        lastAnswered: LastAnsweredFilter,
        riskScore: RiskScoreFilter,
        status: StatusFilter,
        supervisor: ServiceCompanyEmployee | null
    ): KnowYourIndividual[] => {
        let result = [...data];
        // Apply search term filter ONLY if it's not empty and has sufficient length
        if (search.trim() !== '' && search.length >= 2) {
            const searchTermLower = search.toLowerCase().trim();
            result = result.filter((item) => {
                // Search through name fields
                if (item.name.toLowerCase().includes(searchTermLower)) return true;

                // Search through ID
                const companyId = item.ssn;
                if (isSSN(companyId)) {
                    if (companyId.includes(searchTermLower)) return true;
                }

                return false;
            });
        }

        if (lastAnswered !== 'all') {
            // Create separate Date objects for each time period to avoid mutation issues
            const now = new Date();

            // Calculate threeMonthsAgo starting from now
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(now.getMonth() - 3);

            // Calculate eighteenMonthsAgo starting from now (not from threeMonthsAgo!)
            const eighteenMonthsAgo = new Date();
            eighteenMonthsAgo.setMonth(now.getMonth() - 18);

            // Calculate twentyFourMonthsAgo starting from now (not from eighteenMonthsAgo!)
            const twentyFourMonthsAgo = new Date();
            twentyFourMonthsAgo.setMonth(now.getMonth() - 24);

            result = result.filter(item => {
                const lastAnsweredDate = item.questionsAnswered && item.answeredTimestamp
                    ? new Date(item.answeredTimestamp)
                    : null;
                switch (lastAnswered) {
                    case 'exists':
                        return item.questionsAnswered;
                    case '3M':
                        return lastAnsweredDate !== null && lastAnsweredDate >= threeMonthsAgo;
                    case '18M':
                        // Filter for items OLDER than 18 months (before the 18-month threshold)
                        return lastAnsweredDate !== null && lastAnsweredDate < eighteenMonthsAgo;
                    case '24M':
                        // Filter for items OLDER than 24 months (before the 24-month threshold)
                        return lastAnsweredDate !== null && lastAnsweredDate < twentyFourMonthsAgo;
                    case 'never':
                        return !item.questionsAnswered;
                    default:
                        return true;
                }
            });
        }

        if (riskScore !== 'all') {
            result = result.filter(item => {
                const riskScoreValue = item.riskscore ? item.riskscore.split(' ')[0] : '';
                switch (riskScore) {
                    case 'low':
                        return riskScoreValue === 'Lítil';
                    case 'medium':
                        return riskScoreValue === 'Meðal';
                    case 'high':
                        return riskScoreValue === 'Mikil';
                    case 'unfinished':
                        return !item.riskscore;
                    default:
                        return true;
                }
            });
        }

        if (status !== 'all') {
            result = result.filter(item => {
                const statusValue = item.status || '';
                switch (status) {
                    case 'started':
                        return statusValue === 'Könnun hafin' || statusValue === 'Könnun stofnuð';
                    case 'contactSet':
                        return statusValue === 'Tengilið breytt';
                    case 'sent':
                        return statusValue === 'Tilkynning send';
                    case 'answered':
                        return statusValue === 'Könnun svarað';
                    case 'riskDetermined':
                        return statusValue === 'Áhættumat lokið';
                    case 'error':
                        return statusValue === 'Villa';
                    default:
                        return true;
                }
            });
        }
        if (supervisor !== null) {
            result = result.filter(item => item.supervisorId === supervisor.webUserId);
        }

        return result;
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;

        // Clear any existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set the new search term immediately
        setSearchTerm(value);

        // Show loading indicator for search
        setIsLoading(true);

        // Debounce the filter application
        searchTimeoutRef.current = setTimeout(() => {
            updateFiltersAndState(value);
        }, 500);
    };

    const handleLastAnsweredFilterChange = (option: string) => {
        setLastAnsweredFilter(option as LastAnsweredFilter);
    };

    const handleRiskScoreFilterChange = (option: string) => {
        setRiskScoreFilter(option as RiskScoreFilter);
    };

    const handleStatusFilterChange = (option: string) => {
        setStatusFilter(option as StatusFilter);
    };

    const handleSupervisorFilterChange = (option: string | number) => {
        if (option === "all") {
            setSupervisorFilter(null);
        } else {
            const supervisor = employees.find(e => e.webUserId === Number(option));
            setSupervisorFilter(supervisor ?? null);
        }
    };

    const handleCheckboxChange = (individual: KnowYourIndividual, checked: boolean) => {
        setIndividuals(prev => (!prev || prev instanceof Error) ? [individual] :
            prev.map(i =>
                i.userId === individual.userId ? { ...i, checked } : i
            )
        );
    };

    const checkAll = (checked: boolean) => {
        // only check visible/filtered active individuals
        setIndividuals(prev => {
            if (!prev || prev instanceof Error) return [];
            return prev.map(i => {
                if (i.active && filteredData && filteredData.find(fd => fd.userId === i.userId)) {
                    return { ...i, checked };
                }
                return i;
            });
        });
    };

    const displayControlPanel = () => {
        return (
            <div className="control-panel">
                <Button
                    disabled={!anyChecked}
                    onClick={() => setShowKYCOverview(true)}>
                    Hefja könnun
                </Button>
                {
                    !onlyNotificationEligibleChecked
                        ? <Tooltip
                            noUnderline
                            fullHeight
                            text={
                                <Button
                                    disabled>
                                    Senda könnun
                                </Button>}
                            tooltip={"Hefja skal könnun og skrá tengilið til að framkvæma þessa aðgerð."} />
                        : <Button
                            onClick={() => setSendNotificationModal_isOpen(true)}>
                            Senda könnun
                        </Button>
                }
                {
                    !onlyAutoEligibleChecked
                        ? <Tooltip
                            noUnderline
                            fullHeight
                            text={<Button disabled>Sjálfvirkt áhættumat</Button>}
                            tooltip={"Senda skal könnun og móttaka svör til að framkvæma þessa aðgerð."} />
                        : <Button disabled={!onlyAutoEligibleChecked} onClick={() => setAutoRiskScoreModel_isOpen(true)}>Sjálfvirkt áhættumat</Button>

                }
            </div>
        )
    }

    const handleDownloadExcel = async () => {
        try {
            setExcelDownloading(true);
            // Use filtered list if available; otherwise recompute with current filters
            const source: KnowYourIndividual[] = (filteredData && filteredData.length > 0)
                ? filteredData
                : (!individuals || individuals instanceof Error
                    ? []
                    : applyFiltersDirectly(
                        [...individuals],
                        searchTerm,
                        lastAnsweredFilter,
                        riskScoreFilter,
                        statusFilter,
                        supervisorFilter
                    ));

            if (source.length === 0) {
                setToastType('error');
                setToastMessage('Engin gögn til að sækja');
                setToastShow(true);
                return;
            }

            // Collect IDs (individualCustomerId assumed as the identifier for export)
            const ids = source.map(i => i.individualCustomerId);
            const response = await fetch('/DueDiligence/GetIndividualsOverviewExcel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ids)
            });

            if (!response.ok) {
                setToastType('error');
                setToastMessage('Ekki tókst að sækja Excel skrá');
                setToastShow(true);
                return;
            }

            if (response.status === 204) {
                setToastType('error');
                setToastMessage('Excel skrá skilaði engu');
                setToastShow(true);
                return;
            }

            // Create a blob from the response and trigger download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'KYC_Einstaklingar_Yfirlit.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType('error');
            setToastMessage('Villa kom upp við að sækja Excel skrá');
            setToastShow(true);
        } finally {
            setExcelDownloading(false);
        }
    };
    return (
        <>
            <div className="filter-wrapper">
                {/* Search input */}
                <Input
                    clearInput
                    onChange={handleSearchChange}
                    value={searchTerm}
                    className="input-field"
                    label="Leita"
                    withSearchGlassIcon
                />
                {/* Last answered filter */}
                <DropSelect
                    className="dropdown-field"
                    label="Kannanir"
                    options={[
                        { label: "Sýna allt", value: "all" },
                        { label: "Til staðar", value: "exists" },
                        { label: "Yngri en 3 mánaða", value: "3M" },
                        { label: "Eldri en 18 mánaða", value: "18M" },
                        { label: "Eldri en 24 mánaða", value: "24M" },
                        { label: "Ekki til staðar", value: "never" }
                    ]}
                    defaultValue="all"
                    onChange={handleLastAnsweredFilterChange}
                    size="lg"
                />
                {/* Risk score filter */}
                <DropSelect
                    className={"dropdown-field"}
                    label="Áhættumat"
                    options={[
                        { label: "Sýna allt", value: "all" },
                        { label: "Lítil áhætta", value: "low" },
                        { label: "Meðal áhætta", value: "medium" },
                        { label: "Mikil áhætta", value: "high" },
                        { label: "Ólokið", value: "unfinished" },
                    ]}
                    defaultValue={"all"}
                    onChange={handleRiskScoreFilterChange}
                    size="lg"
                />
                {/* Status filter */}
                <DropSelect
                    className={"dropdown-field"}
                    label="Staða"
                    options={[
                        { label: "Sýna allt", value: "all" },
                        { label: "Könnun hafin", value: "started" },
                        { label: "Könnun send", value: "sent" },
                        { label: "Könnun svarað", value: "answered" },
                        { label: "Áhættumat lokið", value: "riskDetermined" },
                        { label: "Villa", value: "error" },
                    ]}
                    defaultValue={"all"}
                    onChange={handleStatusFilterChange}
                    size="lg"
                />
                {/* Supervisor filter */}
                <DropSelect
                    className="dropdown-field"
                    label="Umsjónaraðili"
                    options={[{ label: "Sýna allt", value: "all" }, ...employees.map(e => ({ label: e.fullname, value: e.webUserId}))]}
                    defaultValue="all"
                    onChange={handleSupervisorFilterChange}
                    size="lg"
                />
            </div>
            {isLoading
                ? <Loading />
                : individuals instanceof Error
                    ? <Alert type="error" headText={individuals.message} />
                    : <Table
                        emptyTableText={individuals?.length === 0 ? "Engir einstaklingar fundust. Þú getur bætt við einstakling með hnappinum hér fyrir ofan." : "Engir einstaklingar uppfylla skilyrði síunnar."}
                        showEmptyTableText={true}
                        tableSize="lg"
                        withPagination
                        aSide={displayControlPanel()}
                        bSide={
                            <div className="table-actions-wrapper">

                                <Link
                                    onClick={handleDownloadExcel}
                                    icon={'excel'}
                                    disabled={excelDownloading || !(filteredData && filteredData.length > 0)}
                                >
                                    {'Sækja í Excel'}
                                </Link>
                                <Button
                                    buttonType="secondary"
                                    onClick={() => {
                                        setAddIndividualModal(true);
                                    }}
                                    icon={faPlusCircle}
                                >
                                    Bæta við einstaklingi
                                </Button>
                            </div>
                        }
                        mobileWrapBside
                        rowClassName={(item) => {
                            if (!item.active) return "row-inactive";
                        }}
                        data={filteredData ?? []}
                        columns={[{
                            title: (
                                <Checkbox
                                    name="checkAllIndividuals"
                                    id="checkAllIndividuals"
                                    disabled={!filteredData?.some(c => c.active)}
                                    value={(!!filteredData?.some(c => c.active) && filteredData.filter(c => c.active).every(c => c.checked)).toString()}
                                    onClick={() =>
                                        checkAll(!filteredData?.filter(c => c.active).every(c => c.checked))
                                    }
                                />
                            ),
                            renderCell: (row: KnowYourIndividual) => (
                                <Checkbox
                                    name="kyc-checkbox"
                                    id={`kyc-checkbox-individual-${row.individualCustomerId}`}
                                    disabled={!row.active}
                                    value={(row.checked || false).toString()}
                                    onClick={() =>
                                        handleCheckboxChange(row, !row.checked)
                                    }
                                />
                            ),
                            textAlign: 'left'
                        }, {
                            title: 'Nafn',
                            renderCell: ({ name, individualCustomerId, riskscoreId, status }) => <Link className='center-link' disabled={status === "Villa"} url={`/Areidanleikakannanir/Skoda-Svor-Einstaklingar?riskscoreId=${riskscoreId}&customerId=${individualCustomerId}`}><FontAwesomeIcon icon={faSearchPlus} className='fa-icon-search' />{name}</Link>,
                            textAlign: 'left',
                            simpleSortable: ({ name }) => name.toLowerCase()
                        }, {
                            title: 'Kennitala',
                            renderCell: ({ ssn, country }) => {
                                if (ssn) {
                                    return ssn.replace(/(\d{6})(\d{4})/, '$1-$2');
                                } else if (country) {
                                    const countryData = CountryList.find(c => c.value === country);
                                    return countryData ? countryData.label : '';
                                } else return '';
                            },
                            textAlign: 'left'
                        }, {
                            title: 'Netfang',
                            renderCell: (item) => {
                                // if (item.ssn === null && !item.charged) return null;
                                // if (item.ssn !== null && !item.keldanReportId) return null;
                                if (item.status === "Villa") return null;
                                return <div>
                                    <button
                                        className="edit-info-button"
                                        onClick={async () => {
                                            setEditIndividualModal_data(item);
                                            setEditIndividualModal_isOpen(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                    </button>
                                    {item.email}
                                </div>
                            },
                            textAlign: 'left',
                            simpleSortable: ({ email }) => email === "" ? null : email.toLocaleLowerCase(),
                            sortConfig: { nullHandling: 'always-end' }
                        },
                        {
                            title: 'Könnun hafin',
                            renderCell: ({ riskscoreCreated }) => {
                                if (riskscoreCreated) {
                                    const date = new Date(riskscoreCreated);
                                    return isNaN(date.getTime()) ? '' : getTableDate(date, "DD/MM/YYYY", ".");
                                }
                                return '';
                            },
                            textAlign: 'left',
                            simpleSortable: ({ riskscoreCreated }) => riskscoreCreated ? new Date(riskscoreCreated).getTime() : 0
                        },
                        {
                            title: 'Könnun svarað',
                            renderCell: ({ answeredTimestamp }) => {
                                if (answeredTimestamp) {
                                    const date = new Date(answeredTimestamp);
                                    let tooltipText: string = '';
                                    let tooltipComp = null;
                                    if (date < subMonths(new Date(), 24)) {
                                        tooltipText = 'Áreiðanleikakönnun er eldri en 24 mánaða.';
                                        tooltipComp = <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#DB0033', marginLeft: '5px' }} />;
                                    } else if (date < subMonths(new Date(), 18)) {
                                        tooltipText = 'Áreiðanleikakönnun er eldri en 18 mánaða.';
                                        tooltipComp = <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#F8C513', marginLeft: '5px' }} />;
                                    }
                                    return isNaN(date.getTime()) ? '' : <div>{getTableDate(date, "DD/MM/YYYY", ".")}
                                        {tooltipComp && <Tooltip text={tooltipComp} noUnderline tooltip={tooltipText} />}</div>;
                                }
                                return '';
                            },
                            textAlign: 'left',
                            simpleSortable: ({ answeredTimestamp }) => answeredTimestamp ? new Date(answeredTimestamp).getTime() : 0
                        }, {
                            title: 'Áhættumat',
                            renderCell: ({ questionsAnswered, riskscore, riskscoreId, riskscoreDescription, name }) => {
                                if (!questionsAnswered) return null;
                                const displayString = riskscore ? riskscore.split(' ')[0] : null;
                                let labelType = displayString ? riskToLabelTypeMap[displayString] : null;
                                return <div>
                                    <button
                                        className="edit-info-button"
                                        onClick={() => {
                                            setRiskScoreModal_data({
                                                riskscoreId: riskscoreId ?? null,
                                                riskScore: riskscore,
                                                description: riskscoreDescription,
                                                name: name
                                            });
                                            setRiskScoreModal_isOpen(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                    </button>
                                    {displayString && labelType && (
                                        riskscoreDescription === null || riskscoreDescription === ''
                                            ? <Label
                                                labelType={labelType}
                                                text={displayString}
                                                size="lg"
                                            /> : <Tooltip text={<Label
                                                labelType={labelType}
                                                text={displayString}
                                                size="lg"
                                            />} noUnderline tooltip={riskscoreDescription} />
                                    )}
                                </div>
                            },
                            textAlign: 'left',
                            simpleSortable: ({ riskscore }) => {
                                if (!riskscore) return null;
                                const riskLevelMap: Record<string, number> = { 'Mikil': 3, 'Meðal': 2, 'Lítil': 1 };
                                return riskLevelMap[riskscore.split(' ')[0]] ?? null;
                            },
                            sortConfig: { nullHandling: 'always-end' }
                        }, {
                            title: 'Staða',
                            renderCell: ({ status, riskscoreUpdatedTimestamp, riskscoreCreated }) => {
                                const translatedStatus = translateSurveyStatus(status);

                                if (translatedStatus === "Könnun hafin" && riskscoreUpdatedTimestamp === null) {
                                    return <Tooltip
                                        normalText
                                        delay={{ show: 200, hide: 0 }}
                                        text={translatedStatus}
                                        noUnderline tooltip={riskscoreCreated ? getTableDate(riskscoreCreated, "DD/MM/YYYY HH:MM:SS", ".") : ''}
                                    />;
                                }
                                return riskscoreUpdatedTimestamp !== null ? <Tooltip
                                    normalText
                                    delay={{ show: 200, hide: 0 }}
                                    text={translatedStatus}
                                    noUnderline tooltip={getTableDate(riskscoreUpdatedTimestamp, "DD/MM/YYYY HH:MM:SS", ".")}
                                /> : translatedStatus;
                            },
                            textAlign: 'left',
                            simpleSortable: ({ status }) => {
                                if (!status || status === '' || status === null || status === undefined) {
                                    return '\uffff';
                                }
                                if (status === "Tilkynning send") return 'Könnun send';
                                if (status === "KYC skýrsla sótt" || status === "Könnun stofnuð") return 'Könnun hafin';
                                return status;
                            }
                        },
                        // TODO: ADD THIS BACK LATER WHEN READY
                        // {
                        //     title: 'PEP vöktun',
                        //     renderCell: ({ answeredTimestamp }) => {
                        //         return <Button size='sm'>Vakta</Button>;
                        //     },
                        //     textAlign: 'center'
                        // },

                        {
                            title: 'Allar kannanir',
                            renderCell: ({ riskscoreId, individualCustomerId }) => (
                                riskscoreId
                                    ? <Button buttonType="secondary" size="sm" anchorProps={{ href: `/Areidanleikakannanir/Eldri-Gogn?customerId=${individualCustomerId}&isPerson=true` }}>Skoða</Button>
                                    : ''
                            ),
                            textAlign: 'center'
                        }]}
                    />
            }
            {/* Add Individual Modal */}
            {handleAddIndividual && (
                <AddIndividualModal
                    employees={employees}
                    show={addIndividualShow}
                    close={() => setAddIndividualModal(false)}
                    onAddIndividual={handleAddIndividual}
                    onUpdateIndividual={handleUpdateIndividual}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                />
            )}
            {/* Risk Score Modal */}
            <RiskScoreModal
                show={riskScoreModal_isOpen}
                close={() => setRiskScoreModal_isOpen(false)}
                data={riskScoreModal_data}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                individuals={individuals}
                setIndividuals={setIndividuals}
            />
            <GetKYCOverviewIndividualsModal
                show={showKYCOverviewModal}
                close={() => setShowKYCOverview(false)}
                setIndividuals={setIndividuals}
                selectedIndividuals={individuals instanceof Error || !individuals ? [] : individuals.filter(i => i.checked)}
                individualKYCPrice={individualKYCPrice}
                clearChecked={() => checkAll(false)}
            />
            <EditIndividualModal
                show={editIndividualModal_isOpen}
                employees={employees}
                close={() => setEditIndividualModal_isOpen(false)}
                data={editIndividualModal_data}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                individuals={individuals}
                setIndividuals={setIndividuals}
            />
            {/* Send Notification Modal */}
            <SendNotificationModal
                show={sendNotificationModal_isOpen}
                employees={employees}
                close={() => setSendNotificationModal_isOpen(false)}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                individuals={individuals}
                setIndividuals={setIndividuals}
            />
            {/* Auto Risk Score Modal */}
            <AutoRiskScoreModal
                show={autoRiskScoreModel_isOpen}
                close={() => setAutoRiskScoreModel_isOpen(false)}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                individuals={individuals}
                setIndividuals={setIndividuals}
                type='individual'
            />
            {/* Toast notifications */}
            <ToastContainer className="toastContainer">
                <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={toastShow}>
                    <Alert type={toastType} headText={toastMessage} />
                </Toast>
            </ToastContainer>
        </>
    );
}

export default IndividualsTable;